import {Box} from '@dropbox/dig-foundations';
import {UIIconProps} from '@dropbox/dig-icons';
import {isMobileAtom} from 'atoms/layout';
import cx from 'classnames';
import {ROUTE_PATHS} from 'constant';
import {useRegisterPageDrawer} from 'hooks/useRegisterPageDrawer';
import {useAtomValue} from 'jotai';
import {
  Children,
  createContext,
  CSSProperties,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {Breadcrumb, BreadcrumbLinkProps} from './Breadcrumb';
import {DrawerContainer} from './DrawerContainer';
import styles from './Layout.module.css';
import {
  DSYS_CHROME_TOP_INSET_PX,
  DSYS_FIXED_CHROME_Z_INDEX,
  DSYS_NAVBAR_HEIGHT,
  layoutRootCssVars,
} from './layoutConstants';
import {breakoutWideClassName, InsetScroll, MaxWidth, Page, PageHeader} from './LayoutPage';
import {PresenceFacepile} from './PresenceFacepile';
import {RailSearchRow} from './RailSearchRow';
import {RailTree, RailTreeRow} from './RailTree';

type GridSize = 'default' | '100%';

const GridContext = createContext<{
  withNav: boolean;
}>({withNav: false});

const LayoutRootContext = createContext<RefObject<HTMLDivElement | null> | null>(null);

const Container = ({
  width = 'default',
  breadcrumb,
  // presence,
  children,
}: {
  width?: GridSize;
  breadcrumb?: BreadcrumbLinkProps[];
  presence?: boolean;
  children: ReactNode;
}) => {
  const layoutRootRef = useRef<HTMLDivElement>(null);
  const {withNav, hasHelper, hasFixedHeader} = getContainerChildFlags(children);
  // do not record admin pages in meticulous
  const breadcrumbContainsAdmin = breadcrumb?.some(
    (crumb) => typeof crumb.to === 'string' && crumb.to.includes(ROUTE_PATHS.ADMIN)
  );

  if (!hasHelper) {
    // 100% width
    if (width === '100%') {
      return (
        <LayoutRootContext.Provider value={layoutRootRef}>
          <Box
            ref={layoutRootRef}
            backgroundColor="Background Base"
            padding="32"
            paddingBottom="100"
            display="flex"
            flexDirection="column"
            width="100%"
            style={{
              minHeight: `calc(100vh - ${DSYS_NAVBAR_HEIGHT})`,
              ...(hasFixedHeader ? layoutRootCssVars : {}),
            }}
            className={breadcrumbContainsAdmin ? 'meticulous-ignore' : undefined}
          >
            {breadcrumb && <Breadcrumb path={breadcrumb} />}
            {children}
          </Box>
        </LayoutRootContext.Provider>
      );
    }

    // Condensed
    return (
      <Box
        id="layout-max-width-container"
        padding="32"
        paddingBottom="100"
        backgroundColor="Background Base"
        className={styles.maxWidth + (breadcrumbContainsAdmin ? ' meticulous-ignore' : '')}
        style={{minHeight: 'calc(100vh - 52px)'}}
      >
        <Box width="100%" marginX="auto" style={{maxWidth: 1024}}>
          {/* <Box display="flex" flexDirection="row" alignItems="flex-start"> */}
          {breadcrumb && <Breadcrumb path={breadcrumb} />}
          {/* {presence && <PresenceFacepile />} */}
          {/* </Box> */}
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <GridContext.Provider value={{withNav}}>
      <Box
        backgroundColor="Background Base"
        className={styles.maxWidth}
        width="100%"
        style={{minHeight: 'calc(100vh - 52px)'}}
        display="flex"
        flexDirection="row"
      >
        {/* <Box display="flex" flexDirection="row" alignItems="flex-start"> */}
        {breadcrumb && <Breadcrumb path={breadcrumb} />}
        {/* {presence && <PresenceFacepile />} */}
        {/* </Box> */}
        {children}
      </Box>
    </GridContext.Provider>
  );
};

const Nav = ({children, className}: {children: ReactNode; className?: string}) => {
  const isMobile = useAtomValue(isMobileAtom);

  return (
    <Box
      position="fixed"
      height="100%"
      borderRight="Solid"
      borderColor="Border Subtle"
      backgroundColor="Background Base"
      padding="32"
      marginBottom={isMobile ? '16' : undefined}
      style={{
        width: 'calc(100vw * .35)',
        minWidth: 385,
        maxWidth: 584,
      }}
      className={className}
    >
      {children}
    </Box>
  );
};

type BreadcrumbBuilder = {title: BreadcrumbLinkProps; path: BreadcrumbLinkProps[]};

const isBreadcrumbBuilder = (
  breadcrumb: ReactNode | BreadcrumbBuilder
): breadcrumb is BreadcrumbBuilder => {
  return (
    typeof breadcrumb === 'object' &&
    breadcrumb !== null &&
    'title' in breadcrumb &&
    'path' in breadcrumb
  );
};

const Body = ({
  children,
  breadcrumb,
  className,
  presence,
}: {
  breadcrumb?: ReactNode | BreadcrumbBuilder;
  children: ReactNode;
  className?: string;
  presence?: boolean;
}) => {
  const {withNav} = useContext(GridContext);
  // const isMobile = useAtomValue(isMobileAtom);

  if (!withNav) {
    throw new Error('Body must be a child of a Container with a Nav');
  }

  return (
    <Box
      width="100%"
      style={{
        marginLeft: 'max(min(calc(100vw * .35), 584px), 385px)',
      }}
      className={className}
    >
      <Box
        backgroundColor="Background Base"
        className={styles.maxWidth}
        padding="32"
        style={{minHeight: 'calc(100vh - 52px)'}}
      >
        <Box marginX="auto" style={{maxWidth: 1024}}>
          <Box display="flex" flexDirection="row" alignItems="flex-start">
            {breadcrumb &&
              (isBreadcrumbBuilder(breadcrumb) ? (
                <Breadcrumb path={[breadcrumb.title, ...breadcrumb.path]} />
              ) : (
                breadcrumb
              ))}
            {presence && <PresenceFacepile />}
          </Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

/** Prefer other components to this one */
const Column = ({
  span,
  index,
  // preferComposition,
  children,
}: {
  span: 1 | 2 | 3 | 5 | 7;
  index?: number;
  // preferComposition?: boolean;
  children: ReactNode;
}) => {
  const {withNav} = useContext(GridContext);
  const size = withNav ? 7 : 5;
  const startColumn = index !== undefined ? index + 1 : Math.floor((size - span) / 2) + 1;

  return (
    <Box
      // paddingX={preferComposition ? undefined : '16'}
      style={{gridColumn: `${startColumn} / span ${span}`}}
    >
      {children}
    </Box>
  );
};

const InlineDrawerContainer = ({
  open,
  onClose,
  width = 'default',
  breadcrumb,
  // presence,
  childSheet,
  drawerIcon,
  drawerHeader,
  drawerBody,
  drawerFooter,
  drawerWidth,
  children,
  preventEscapeClose,
  containerRef,
  pushContent = false,
  fillDrawerHeader,
  disableAnimation = false,
}: {
  open: boolean;
  childSheet?: {
    icon?: UIIconProps['src'];
    header: ReactNode;
    body: ReactNode;
    onClose: () => void;
  };
  onClose?: () => void;
  breadcrumb?: BreadcrumbLinkProps[];
  presence?: boolean;
  width?: GridSize;
  drawerHeader: ReactNode;
  drawerIcon?: UIIconProps['src'];
  drawerBody: ReactNode;
  drawerFooter?: ReactNode;
  preventEscapeClose?: boolean;
  children?: ReactNode;
  drawerWidth?: CSSProperties['width'];
  containerRef?: React.RefObject<HTMLDivElement>;
  pushContent?: boolean;
  fillDrawerHeader?: boolean;
  disableAnimation?: boolean;
}) => {
  const renderContent = !!children || !!breadcrumb;
  const effectiveDrawerWidth = drawerWidth || '420px';

  useRegisterPageDrawer(open);

  // Shared drawer content components
  const mainDrawerContent = (
    <DrawerContainer
      header={drawerHeader}
      footer={drawerFooter}
      icon={drawerIcon}
      open={open}
      onClose={onClose}
      preventEscapeClose={preventEscapeClose}
      fillHeader={fillDrawerHeader}
    >
      {drawerBody}
    </DrawerContainer>
  );

  const childSheetContent = childSheet && (
    <DrawerContainer
      backButton
      header={childSheet.header}
      icon={childSheet.icon}
      open
      onClose={childSheet.onClose}
    >
      {childSheet.body}
    </DrawerContainer>
  );

  const {withNav: withNavInDrawer} = getContainerChildFlags(children ?? null);

  // Shared main content
  const mainContent = renderContent && (
    <GridContext.Provider value={{withNav: withNavInDrawer}}>
      <Box className={cx(styles.container)}>
        <Container width={width}>
          {breadcrumb && <Breadcrumb path={breadcrumb} />}
          {children}
        </Container>
      </Box>
    </GridContext.Provider>
  );

  if (pushContent) {
    return (
      <>
        <Box
          display="flex"
          flexDirection="row"
          style={{
            minHeight: '100vh',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            style={{
              flex: 1,
              transition: disableAnimation ? 'none' : 'margin-right 0.3s ease',
              marginRight: open ? effectiveDrawerWidth : '0',
              overflow: 'auto',
            }}
          >
            {mainContent}
          </Box>

          <Box
            ref={containerRef}
            className={styles.drawer}
            style={{
              width: effectiveDrawerWidth,
              transform: open ? 'translateX(0)' : 'translateX(100%)',
              transition: disableAnimation ? 'none' : 'transform 0.3s ease',
              right: 0,
            }}
          >
            {mainDrawerContent}
          </Box>

          {/* Child sheet */}
          {childSheet && (
            <Box
              className={cx(styles.drawer, styles.childDrawer)}
              style={{
                width: effectiveDrawerWidth,
                transform: childSheet.body ? 'translateX(0)' : 'translateX(100%)',
                transition: disableAnimation ? 'none' : undefined,
                right: 0,
              }}
            >
              {childSheetContent}
            </Box>
          )}
        </Box>
      </>
    );
  }

  // Original CSS overlay drawer
  return (
    <>
      <Box className={open ? styles.open : undefined}>
        {mainContent}

        <Box
          ref={containerRef}
          className={styles.drawer}
          style={
            drawerWidth
              ? {
                  width: drawerWidth,
                  right: open ? '0' : `-${drawerWidth}`,
                  transition: disableAnimation ? 'none' : undefined,
                }
              : undefined
          }
        >
          {mainDrawerContent}
        </Box>

        <Box
          className={cx(styles.drawer, styles.childDrawer, {
            [styles.childDrawerOpen]: childSheet?.body,
          })}
          style={
            drawerWidth
              ? {
                  width: drawerWidth,
                  right: open ? '0' : `-${drawerWidth}`,
                  transition: disableAnimation ? 'none' : undefined,
                }
              : undefined
          }
        >
          {childSheetContent}
        </Box>
      </Box>
    </>
  );
};

const Header = ({
  children,
  showScrollShadow = true,
}: {
  children: ReactNode;
  showScrollShadow?: boolean;
}) => {
  const layoutRootRef = useContext(LayoutRootContext);
  const chromeRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Show a soft bottom fade once the page has scrolled under the fixed header,
  // signaling there's content above (mirrors the Table edge-fade affordance).
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const updateScrolled = () => {
      setIsScrolled(window.scrollY > 1);
    };

    updateScrolled();
    window.addEventListener('scroll', updateScrolled, {passive: true});
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  useLayoutEffect(() => {
    const chromeElement = chromeRef.current;
    const layoutRoot = layoutRootRef?.current;
    if (!chromeElement || !layoutRoot) {
      return;
    }

    const publishChromeHeight = () => {
      const fullHeight = chromeElement.getBoundingClientRect().height;
      const flowHeight = Math.max(0, fullHeight - DSYS_CHROME_TOP_INSET_PX);
      layoutRoot.style.setProperty('--dsys-fixed-chrome-height', `${flowHeight}px`);
      layoutRoot.style.setProperty(
        '--dsys-sticky-offset-top',
        `calc(var(--dsys-navbar-height, ${DSYS_NAVBAR_HEIGHT}) + ${fullHeight}px)`
      );
    };

    publishChromeHeight();
    const remeasureFrame = requestAnimationFrame(publishChromeHeight);

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', publishChromeHeight);
      return () => {
        cancelAnimationFrame(remeasureFrame);
        window.removeEventListener('resize', publishChromeHeight);
      };
    }

    const resizeObserver = new ResizeObserver(publishChromeHeight);
    resizeObserver.observe(chromeElement);
    return () => {
      cancelAnimationFrame(remeasureFrame);
      resizeObserver.disconnect();
    };
  }, [children, layoutRootRef]);

  return (
    <>
      <Box
        ref={chromeRef}
        position="fixed"
        backgroundColor="Background Base"
        paddingX="32"
        paddingTop="32"
        paddingBottom="16"
        style={{
          top: 'var(--dsys-navbar-height, 52px)',
          left: 'var(--dsys-main-offset-left, 56px)',
          right: 0,
          zIndex: DSYS_FIXED_CHROME_Z_INDEX,
          boxSizing: 'border-box',
        }}
      >
        {children}
        {showScrollShadow && (
          <div
            aria-hidden
            className={cx(styles.headerScrollShadow, {
              [styles.headerScrollShadowVisible]: isScrolled,
            })}
          />
        )}
      </Box>
      <Box
        width="100%"
        flexShrink={0}
        aria-hidden
        style={{height: 'var(--dsys-fixed-chrome-height)'}}
      />
    </>
  );
};

function getContainerChildFlags(children: ReactNode) {
  const childArray = Children.toArray(children);
  return {
    withNav: childArray.some((child: any) => child.type === Nav),
    hasHelper: childArray.some((child: any) => [Nav, Body, Column].includes(child.type)),
    hasFixedHeader: childArray.some((child: any) => child.type === Header),
  };
}

export const Layout = {
  Container,
  Header,
  Nav,
  Body,
  Column,
  InlineDrawerContainer,
  Page,
  PageHeader,
  MaxWidth,
  InsetScroll,
  breakoutWideClassName,
  RailTree,
  RailTreeRow,
  RailSearchRow,
};
